import React from "react";
import {
  HealthCheckup,
  MedicalSummaryData,
  MedicationItem,
} from "../../types/medical-summary";
import { Button } from "../catalyst/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../catalyst/dialog";
import Icon from "../ui/Icon";

interface MedicalSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: MedicalSummaryData | null;
  isLoading: boolean;
  error: string | null;
}

const SectionHeader: React.FC<{ title: string; icon: string }> = ({
  title,
  icon,
}) => (
  <div className="flex items-center space-x-2 mb-2">
    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size="sm" className="text-blue-600" />
    </div>
    <h3 className="text-xs font-semibold text-text-primary">{title}</h3>
  </div>
);

const EmptySection: React.FC<{ message: string }> = ({ message }) => (
  <p className="text-xs text-text-tertiary py-1">{message}</p>
);

/**
 * 백엔드에서 단일 문자열로 내려오는 의약품 정보를 파싱한다.
 *
 * 예상 형식: "의약품명(성분명)_(용량)"  e.g. "타이레놀정500mg(아세트아미노펜)_(1일 3회)"
 *   - 성분명은 괄호(), 대괄호[] 안에 올 수 있음
 *   - 용량은 언더스코어 뒤 괄호 _(...) 로 전달됨
 *
 * NOTE: 이 파싱 로직은 백엔드 문자열 형식에 강하게 결합되어 있어,
 * 형식이 변경되면 UI가 깨질 수 있다. 장기적으로는 백엔드 DTO를
 * 구조화된 객체(name, ingredient, dosage)로 분리하는 것이 바람직하다.
 */
function parseMedicationName(raw: string): {
  name: string;
  ingredient?: string;
  dose?: string;
} {
  let remaining = raw;
  let dose: string | undefined;

  const doseMatch = remaining.match(/_\(([^)]+)\)\s*$/);
  if (doseMatch) {
    dose = doseMatch[1];
    remaining = remaining.slice(0, remaining.length - doseMatch[0].length);
  }

  let ingredient: string | undefined;
  let name = remaining;
  let bracketDepth = 0;

  for (let i = 0; i < remaining.length; i++) {
    const ch = remaining[i];
    if (ch === "[") bracketDepth++;
    else if (ch === "]") bracketDepth--;
    else if (ch === "(" && bracketDepth === 0) {
      const closeIdx = remaining.indexOf(")", i + 1);
      if (closeIdx > i) {
        name = remaining.slice(0, i);
        ingredient = remaining.slice(i + 1, closeIdx);
        break;
      }
    }
  }

  if (!ingredient) {
    const bracketMatch = name.match(/^(.+?)\[([^\]]+)\]$/);
    if (bracketMatch) {
      name = bracketMatch[1];
      ingredient = bracketMatch[2];
    }
  }

  name = name.replace(/_+$/, "").trim();

  return { name, ingredient, dose };
}

const ChatSummarySection: React.FC<{ summary: string }> = ({ summary }) => (
  <div>
    <SectionHeader title="대화 문진 요약" icon="message-circle" />
    <p className="text-xs text-text-primary leading-relaxed whitespace-pre-wrap pl-8">
      {summary}
    </p>
  </div>
);

const MedicationTable: React.FC<{
  title: string;
  icon: string;
  medications: MedicationItem[];
  emptyMessage: string;
}> = ({ title, icon, medications, emptyMessage }) => (
  <div>
    <SectionHeader title={title} icon={icon} />
    {medications.length === 0 ? (
      <EmptySection message={emptyMessage} />
    ) : (
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-100 text-text-tertiary">
            <th className="text-left py-1.5 px-2 font-medium">의약품명</th>
            <th className="text-left py-1.5 px-2 font-medium whitespace-nowrap">
              용량
            </th>
            <th className="text-left py-1.5 px-2 font-medium whitespace-nowrap">
              처방일
            </th>
          </tr>
        </thead>
        <tbody>
          {medications.map((med, idx) => {
            const parsed = parseMedicationName(med.medicationName);
            return (
              <tr
                key={`${med.medicationName}-${med.dispensedDate}-${idx}`}
                className="border-b border-gray-100"
              >
                <td className="py-1.5 px-2">
                  <span className="text-text-primary">{parsed.name}</span>
                  {parsed.ingredient && (
                    <span className="block text-[10px] text-text-tertiary">
                      {parsed.ingredient}
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2 text-text-secondary whitespace-nowrap align-top">
                  {parsed.dose || "-"}
                </td>
                <td className="py-1.5 px-2 text-text-secondary whitespace-nowrap align-top">
                  {med.dispensedDate}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

function groupObservations(
  observations: { code: string; value: string }[]
): { code: string; values: string[] }[] {
  const map = new Map<string, Set<string>>();
  const order: string[] = [];
  for (const obs of observations) {
    if (!map.has(obs.code)) {
      map.set(obs.code, new Set());
      order.push(obs.code);
    }
    map.get(obs.code)!.add(obs.value);
  }
  return order.map((code) => ({ code, values: [...map.get(code)!] }));
}

const HealthCheckupTable: React.FC<{
  title: string;
  icon: string;
  checkup?: HealthCheckup;
  emptyMessage: string;
  grouped?: boolean;
}> = ({ title, icon, checkup, emptyMessage, grouped = false }) => (
  <div>
    <SectionHeader title={title} icon={icon} />
    {!checkup || checkup.observations.length === 0 ? (
      <EmptySection message={emptyMessage} />
    ) : (
      <>
        <p className="text-[10px] text-text-tertiary mb-1 pl-8">
          수집일: {checkup.checkupDate}
        </p>
        {grouped ? (
          <div className="space-y-2">
            {groupObservations(checkup.observations).map((group) => (
              <div
                key={group.code}
                className="bg-gray-50 rounded-lg px-3 py-2"
              >
                <p className="text-[11px] font-semibold text-blue-700 mb-1">
                  {group.code}
                </p>
                {group.values.map((val, vi) => (
                  <p
                    key={vi}
                    className="text-xs text-text-primary leading-relaxed"
                  >
                    {val}
                  </p>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100 text-text-tertiary">
                <th className="text-left py-1.5 px-2 font-medium">항목</th>
                <th className="text-left py-1.5 px-2 font-medium">결과</th>
              </tr>
            </thead>
            <tbody>
              {checkup.observations.map((obs, idx) => (
                <tr
                  key={`${obs.code}-${idx}`}
                  className="border-b border-gray-100"
                >
                  <td className="py-1.5 px-2 text-text-secondary">
                    {obs.code}
                  </td>
                  <td className="py-1.5 px-2 text-text-primary font-medium">
                    {obs.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {checkup.findings.length > 0 && (
          <div className="mt-2 pl-8">
            <p className="text-[10px] font-medium text-text-tertiary mb-1">
              종합소견
            </p>
            {checkup.findings.map((f, idx) => (
              <p key={`finding-${idx}`} className="text-xs text-text-primary">
                <span className="text-blue-600 font-medium">{f.code}:</span>{" "}
                {f.value}
              </p>
            ))}
          </div>
        )}
      </>
    )}
  </div>
);

const MedicalSummaryModal: React.FC<MedicalSummaryModalProps> = ({
  isOpen,
  onClose,
  data,
  isLoading,
  error,
}) => {
  const isEmpty =
    data &&
    !data.chatSummary &&
    data.currentMedications.length === 0 &&
    data.recentMedications.length === 0 &&
    !data.latestHealthCheckup &&
    !data.latestCancerScreening;

  return (
    <Dialog open={isOpen} onClose={onClose} size="lg">
      <div className="flex items-center justify-between">
        <DialogTitle>문진요약서</DialogTitle>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="x" size="md" className="text-text-secondary" />
        </button>
      </div>

      <DialogBody>
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <svg
              className="w-8 h-8 text-blue-500 animate-spin mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <p className="text-sm text-text-secondary">
              문진요약서를 생성하고 있습니다...
            </p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="x" size="lg" className="text-red-600" />
            </div>
            <p className="text-sm text-text-secondary">{error}</p>
          </div>
        )}

        {!isLoading && !error && isEmpty && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Icon name="document" size="lg" className="text-gray-400" />
            </div>
            <p className="text-sm text-text-secondary">
              연동된 의료 데이터가 없습니다.
            </p>
            <p className="text-xs text-text-tertiary mt-1">
              설정에서 내 의료기록을 연동해주세요.
            </p>
          </div>
        )}

        {!isLoading && !error && data && !isEmpty && (
          <div className="space-y-4">
            {data.chatSummary && (
              <ChatSummarySection summary={data.chatSummary} />
            )}

            <MedicationTable
              title="현재 투약 중인 의약품"
              icon="activity"
              medications={data.currentMedications}
              emptyMessage="현재 투약 중인 의약품이 없습니다."
            />

            <MedicationTable
              title="과거 투약 이력"
              icon="clock"
              medications={data.recentMedications}
              emptyMessage="과거 투약 이력이 없습니다."
            />

            <HealthCheckupTable
              title="일반 건강검진"
              icon="chart"
              checkup={data.latestHealthCheckup}
              emptyMessage="건강검진 데이터가 없습니다."
            />

            <HealthCheckupTable
              title="암 건강검진"
              icon="shield"
              checkup={data.latestCancerScreening}
              emptyMessage="암 건강검진 데이터가 없습니다."
              grouped
            />

            {data.generatedAt && (
              <p className="text-xs text-text-tertiary text-right">
                생성일시:{" "}
                {new Date(data.generatedAt).toLocaleString("ko-KR")}
              </p>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        <Button outline onClick={onClose}>
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalSummaryModal;
